<template>
    <div class="CriterionBooleanInput">
        <FormElement :label="hideTitle ? null : form.schema.title">
            <template v-if="form.schema.description" #labelHint>
                <div
                    v-tooltip="form.schema.description"
                    class="CriterionEnumInput__hintIcon"
                >
                    <FontAwesomeIcon :icon="faQuestionCircle" />
                </div>
            </template>
            <FormInput
                :invalid="showValidationErrors && (showError || accessed) && hasError"
                force-error-slot
                force-action-slot
            >
                <Dropdown
                    :options="booleanOptions"
                    :selected="convertBoolToOptions(value)"
                    :disabled="rbacDisabled"
                    @input="onBooleanFieldChange($event)"
                />
                <template v-if="showValidationErrors && (showError || accessed) && error" #errorMessage>
                    {{ error }}
                </template>
                <template #actionButtons>
                    <LockInputIcon v-if="rbacDisabled" />
                    <NewButton
                        v-else-if="deletable"
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <slot name="deleteIcon">
                            <EidIcon :icon-src="faXmark" alt="delete" />
                        </slot>
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { Component } from '@evidentid/vue-property-decorator';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import Dropdown from '@evidentid/dashboard-commons/components/Dropdown/Dropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaBoolean } from '@evidentid/json-schema/interfaces/JsonSchema';

    import NewButton from '@/components/new-button/NewButton.vue';
    import AbstractCriterionInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/AbstractCriterionInput';

    type JsonFormBoolean = JsonFormBase<JsonSchemaBoolean>;

    @Component({
        emits: [ 'delete', 'input' ],
        components: {
            Dropdown, EidIcon, FormInput, FormElement, FontAwesomeIcon, LockInputIcon, NewButton,
        },
    })
    export default class CriterionBooleanInput extends AbstractCriterionInput<JsonFormBoolean, boolean> {
        private faXmark = faXmark;
        private faQuestionCircle = faQuestionCircle;

        private booleanOptions: DropdownOption[] = [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
        ];

        /** Only a real boolean names an option. A Yes/No value that reached this control as the
         * "YES"/"NO" text would otherwise read as Yes whatever it said, every non-empty string
         * being truthy; showing nothing selected leaves the validation error it has already
         * failed as the thing the operator acts on.
         */
        private convertBoolToOptions(value: boolean | undefined): DropdownOption[] {
            return typeof value === 'boolean' ? [ { label: value ? 'Yes' : 'No', value } ] : [];
        }

        private onBooleanFieldChange(option: DropdownOption): void {
            this.$emit('input', option.value);
        }
    }
</script>
